(() => {
  if (customElements.get('py-look37-preview')) return;
  const catalogs = new Map();
  const text = value => typeof value === 'string' ? value : '';
  const element = (tag, className, value) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (value !== undefined) node.textContent = String(value);
    return node;
  };
  const safeImage = value => {
    try {
      const url = new URL(text(value), location.origin);
      return url.protocol === 'https:' ? url.href : '';
    } catch { return ''; }
  };
  const productUrl = value => {
    try {
      const url = new URL(text(value), location.origin);
      return url.origin === location.origin && /\/products\/[^/]+\/?$/.test(url.pathname) ? url.href : '';
    } catch { return ''; }
  };
  const photo = (source, title, className, eager = false) => {
    const frame = element('div', className);
    const url = safeImage(source);
    if (url) {
      const img = element('img');
      img.src = url;
      img.alt = text(title);
      img.width = 1200;
      img.height = 1200;
      img.loading = eager ? 'eager' : 'lazy';
      img.decoding = 'async';
      if (eager) img.fetchPriority = 'high';
      frame.append(img);
    }
    return frame;
  };
  customElements.define('py-look37-preview', class extends HTMLElement {
    async connectedCallback() {
      if (this.started) return;
      this.started = true;
      const section = this.closest('.shopify-section') || this.parentElement;
      this.section = section;
      const handle = new URLSearchParams(location.search).get('look_preview');
      if (this.dataset.mode === 'detail' && !handle) {
        const base = this.baseUrl();
        for (const link of section.querySelectorAll('a[href]')) {
          const url = new URL(link.href, location.origin);
          if (url.origin === base.origin && url.pathname === base.pathname) link.href = base.href;
        }
        return;
      }
      if (this.dataset.mode === 'detail') {
        this.original = section.querySelector('[data-look37-original]');
        if (this.original) this.original.hidden = true;
        this.hidden = false;
        this.status = element('p', 'py-look37-stage__status', 'Loading look…');
        this.status.setAttribute('role', 'status');
        this.append(this.status);
      }
      try {
        const url = this.dataset.catalogUrl;
        if (!catalogs.has(url)) {
          catalogs.set(url, fetch(url, { credentials: 'same-origin' }).then(response => {
            if (!response.ok) throw new Error('catalog');
            return response.json();
          }).then(data => {
            if (data.schema_version !== 1 || !Array.isArray(data.looks)) throw new Error('catalog');
            return data.looks.filter(look => /^[a-z0-9][a-z0-9-]*$/.test(text(look.handle)) && text(look.title));
          }).catch(error => { catalogs.delete(url); throw error; }));
        }
        this.looks = await catalogs.get(url);
        if (!this.isConnected) return;
        if (this.dataset.mode === 'detail') {
          const look = this.looks.find(candidate => candidate.handle === handle);
          if (!look) throw new Error('missing-look');
          this.renderDetail(look);
        } else this.renderGrid();
        this.dataset.loaded = 'true';
      } catch {
        if (this.dataset.mode === 'detail') {
          this.status.textContent = 'This look preview is unavailable. Return to Shop the Look to choose another look.';
          const back = element('a', 'py-look37-stage__back', 'Back to Shop the Look');
          back.href = this.baseUrl().href;
          this.append(back);
        } else {
          this.hidden = false;
          this.append(element('p', 'py-look37-stage__status', 'New look previews could not load. Refresh this page to try again.'));
        }
        this.dataset.loaded = 'error';
      }
    }
    baseUrl() {
      const url = new URL(this.dataset.baseUrl || '/collections/ear-stacks', location.origin);
      return this.withPreview(url);
    }
    withPreview(url) {
      const theme = new URLSearchParams(location.search).get('preview_theme_id');
      if (theme && /^\d+$/.test(theme)) url.searchParams.set('preview_theme_id', theme);
      return url;
    }
    canonicalUrl(look) {
      if (!look || look.online_store_published !== true) return null;
      try {
        const url = new URL(text(look.canonical_url), location.origin);
        if (url.origin !== location.origin || url.pathname !== `/collections/${look.handle}`) return null;
        url.searchParams.delete('view');
        url.searchParams.delete('look_preview');
        return this.withPreview(url);
      } catch { return null; }
    }
    detailUrl(handle) {
      const canonical = this.canonicalUrl(this.looks.find(look => look.handle === handle));
      if (canonical) return canonical.href;
      const url = this.baseUrl();
      url.searchParams.set('view', 'py-look-collection-v6');
      url.searchParams.set('look_preview', handle);
      return url.href;
    }
    renderGrid() {
      const grid = this.section.querySelector('[data-py-look-grid]');
      if (!grid) throw new Error('grid');
      if (!this.looks.length) throw new Error('empty-catalog');
      const nativeCards = new Map();
      for (const card of grid.querySelectorAll('[data-py-look-card]')) {
        if (card.hasAttribute('data-py-look-fallback')) continue;
        const link = card.querySelector('a');
        if (!link) continue;
        try {
          const url = new URL(link.href, location.origin);
          const match = url.pathname.match(/\/collections\/([a-z0-9][a-z0-9-]*)\/?$/);
          if (url.origin === location.origin && match && !nativeCards.has(match[1])) nativeCards.set(match[1], card);
        } catch {}
      }
      const fragment = document.createDocumentFragment();
      const present = new Set();
      for (const look of this.looks) {
        if (present.has(look.handle)) continue;
        present.add(look.handle);
        const canonical = this.canonicalUrl(look);
        const nativeCard = canonical ? nativeCards.get(look.handle) : null;
        if (nativeCard) {
          nativeCard.dataset.look37Handle = look.handle;
          nativeCard.dataset.pyLookTags = Array.isArray(look.filters) ? look.filters.map(text).join(' ').toLowerCase() : nativeCard.dataset.pyLookTags || '';
          nativeCard.querySelector('a').href = canonical.href;
          fragment.append(nativeCard);
          continue;
        }
        const card = element('article', 'py-look-v6__card');
        card.dataset.pyLookCard = '';
        card.dataset.pyLookTags = Array.isArray(look.filters) ? look.filters.map(text).join(' ').toLowerCase() : '';
        card.dataset.look37Handle = look.handle;
        const link = element('a', 'py-look-v6__link');
        link.href = this.detailUrl(look.handle);
        link.setAttribute('aria-label', look.title);
        const image = photo(look.image, look.title, 'py-look-v6__media');
        if (!canonical) image.append(element('span', 'py-look-v6__badge', 'COMING SOON'));
        const content = element('div', 'py-look-v6__content');
        content.append(element('h2', '', look.title));
        const meta = element('div', 'py-look-v6__meta');
        meta.append(element('span', '', `${Array.isArray(look.items) ? look.items.length : 0} SELECTIONS`));
        const arrow = element('span', 'py-look-v6__arrow', '→');
        arrow.setAttribute('aria-hidden', 'true');
        meta.append(arrow);
        content.append(meta);
        link.append(image, content);
        card.append(link);
        fragment.append(card);
      }
      for (const [handle, card] of nativeCards) {
        if (present.has(handle)) continue;
        present.add(handle);
        card.dataset.look37Handle = handle;
        card.querySelector('a').href = this.withPreview(new URL(card.querySelector('a').href, location.origin)).href;
        fragment.append(card);
      }
      grid.replaceChildren(fragment);
      const activeFilter = this.section.querySelector('[data-py-look-filter].is-active');
      if (activeFilter) activeFilter.click();
    }
    renderDetail(look) {
      this.replaceChildren();
      this.className = 'py-look37-stage page-width';
      this.dataset.look37Handle = look.handle;
      document.title = `${look.title} | Shop the Look | PUNKYOUTH`;
      const nav = element('nav', 'py-look37-stage__breadcrumb');
      nav.setAttribute('aria-label', 'Breadcrumb');
      const back = element('a', '', 'Shop the Look');
      back.href = this.baseUrl().href;
      nav.append(back, element('span', '', '/'));
      const current = element('span', '', look.title);
      current.setAttribute('aria-current', 'page');
      nav.append(current);
      const layout = element('div', 'py-look37-stage__layout');
      const gallery = element('div', 'py-look37-stage__gallery');
      gallery.append(photo(look.image, look.title, 'py-look37-stage__hero', true));
      const information = element('div', 'py-look37-stage__information');
      information.append(element('p', 'py-look37-stage__eyebrow', 'SHOP THE LOOK'), element('h1', '', look.title));
      if (text(look.description)) information.append(element('p', 'py-look37-stage__description', look.description));
      const notice = element('div', 'py-look37-stage__notice');
      const pricingPending = !Array.isArray(look.items) || look.items.some(item => item.draft !== false || !Number.isFinite(item.price));
      notice.append(element('strong', '', 'COMING SOON'), element('p', '', pricingPending ? 'Explore the pieces in this ear stack. Product details and pricing are being prepared.' : 'Explore the pieces in this ear stack and shop each available item individually.'));
      information.append(notice, element('h2', 'py-look37-stage__pieces-heading', 'PIECES IN THIS LOOK'));
      const items = element('div', 'py-look37-stage__items');
      for (const item of Array.isArray(look.items) ? look.items : []) {
        const card = element('article', 'py-look37-stage__item');
        card.dataset.look37Item = '';
        card.append(photo(item.image, item.title, 'py-look37-stage__item-image'));
        const body = element('div', 'py-look37-stage__item-body');
        body.append(element('h3', '', text(item.title)));
        if (text(item.variant)) body.append(element('p', 'py-look37-stage__variant', item.variant));
        if (text(item.unit_label)) body.append(element('p', 'py-look37-stage__unit', item.unit_label));
        if (Number.isInteger(item.quantity) && item.quantity > 1) body.append(element('p', 'py-look37-stage__quantity', `Quantity: ${item.quantity}`));
        body.append(element('p', 'py-look37-stage__pending', item.draft === false ? 'Part of this upcoming look' : 'Coming soon'));
        const itemUrl = item.draft === false ? productUrl(item.product_url) : '';
        if (itemUrl) {
          const link = element('a', 'py-look37-stage__product-link', 'VIEW PRODUCT ↗');
          link.href = this.withPreview(new URL(itemUrl)).href;
          body.append(link);
        }
        card.append(body);
        items.append(card);
      }
      information.append(items);
      const button = element('button', 'py-look37-stage__coming-soon', pricingPending ? 'COMING SOON · PRICING PENDING' : 'COMING SOON');
      button.type = 'button';
      button.disabled = true;
      information.append(button);
      layout.append(gallery, information);
      this.append(nav, layout);
      const more = element('section', 'py-look37-stage__more');
      more.append(element('h2', '', 'MORE LOOKS TO EXPLORE'));
      const moreGrid = element('div', 'py-look37-stage__more-grid');
      for (const other of this.looks.filter(candidate => candidate.handle !== look.handle).slice(0, 3)) {
        const link = element('a', 'py-look37-stage__more-card');
        link.href = this.detailUrl(other.handle);
        link.append(photo(other.image, other.title, 'py-look37-stage__more-image'), element('h3', '', other.title));
        moreGrid.append(link);
      }
      more.append(moreGrid);
      this.append(more);
    }
  });
})();
